import { CUSTOM_FIELD_TYPES } from "@/types/customFieldTypes";
import { ParameterInterface, ParameterType, ParameterSubjectType, ParameterInputType, ParameterParams, SINGLE_VALUE_INPUT_TYPES, MULTIPLE_VALUES_INPUT_TYPES, CHOICE_INPUT_TYPES } from "@/types/parameterTypes";
import { CustomFieldBuilder } from "../access/customFields/customFieldBuilder";
import { CustomField } from "./customFields/customField";
import { ObjectCustomField } from "./customFields/objectCustomField";
import { ObjectKeyProperty } from "./objectInstance";

export class Parameter implements ParameterInterface {
  id: string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  type: ParameterType;
  title: string;
  subjectId: string;
  subjectType: ParameterSubjectType;
  identifier: string;
  position: number;
  fields: CustomField[];
  sourceId: string;
  inputType: ParameterInputType;
  viewOnly: boolean;
  parentId: string;
  fieldType: CUSTOM_FIELD_TYPES;
  description?: string;
  allowCustomChoices: boolean;

  constructor(params: ParameterParams) {
    this.id = params.id;
    this.createdAt = params.createdAt;
    this.updatedAt = params.updatedAt;
    this.type = params.type;
    this.title = params.title || '';
    this.subjectId = params.subjectId;
    this.subjectType = params.subjectType;
    this.identifier = params.identifier || '';
    this.position = params.position;
    this.fields = params.fields.map((field) => new CustomFieldBuilder().build(field));
    this.sourceId = params.sourceId || '';
    this.inputType = params.inputType;
    this.viewOnly = params.viewOnly || false;
    this.parentId = params.subjectId;
    this.fieldType = this.fields[0]?.fieldType;
    this.description = params.description;
    this.allowCustomChoices = params.allowCustomChoices;
  }

  hasMultipleValues() {
    return MULTIPLE_VALUES_INPUT_TYPES.includes(this.inputType);
  }

  hasSingleValue() {
    return SINGLE_VALUE_INPUT_TYPES.includes(this.inputType);
  }

  isPrimitive() {
    return this.inputType === ParameterInputType.FREE_FORM && !(this.fields[0] instanceof ObjectCustomField);
  }

  isChoice() {
    return CHOICE_INPUT_TYPES.includes(this.inputType);
  }

  isArray() {
    return this.inputType === ParameterInputType.LIST;
  }

  concatArray() {
    return this.getFields().map((field) => field.asString()).join(', ');
  }

  isValidatable() {
    if (this.subjectType !== ParameterSubjectType.STEP) {
      return false;
    }

    return this.fieldType === CUSTOM_FIELD_TYPES.Float
    || this.fieldType === CUSTOM_FIELD_TYPES.Integer
    || this.fieldType === CUSTOM_FIELD_TYPES.String
    || this.fieldType === CUSTOM_FIELD_TYPES.Boolean;
  }

  getFields() {
    return this.fields.filter(field => this.isChoice() ? field.isSelected : true);
  }

  hasEmptyField() {
    if (this.isChoice()) {
      return this.fields.find(field => field.isSelected) === undefined;
    }
    return this.fields.some(field => field.isEmpty());
  }

  hasRootSubject() {
    return this.subjectType === ParameterSubjectType.PROCEDURE
      || this.subjectType === ParameterSubjectType.IMPLEMENTATION;
  }

  get propertyKeys(): ObjectKeyProperty[] {
    return Parameter.propertyKeys(this.fields);
  }

  get objectShapeId() {
    if (this.fields[0] instanceof ObjectCustomField) {
      return this.fields[0].objectInstance.objectShapeId;
    }
    return undefined;
  }

  static propertyKeys(fields: CustomField[]): ObjectKeyProperty[] {
    if (fields[0] instanceof ObjectCustomField) {
      return fields[0].objectInstance.properties;
    }
    return [];
  }

  hasEquivalentShape(other: Parameter) {
    const sortedPropertyKeys = this.propertyKeys.sort((a, b) => a.key.localeCompare(b.key));
    const sortedOtherPropertyKeys = other.propertyKeys.sort((a, b) => a.key.localeCompare(b.key));
  
    return this.fieldType === other.fieldType 
    && this.identifier === other.identifier
    && this.inputType === other.inputType
    && this.type === other.type
    && sortedPropertyKeys.every((key, index) => key.key === sortedOtherPropertyKeys[index].key && key.type === sortedOtherPropertyKeys[index].type);
  }
 }
